import { useState, useEffect, useCallback } from 'react'
import { apiGet, getErrorMessage } from '../utils/api'

export interface AgentStats {
  id: string
  name: string
  email: string
  status: 'online' | 'offline' | 'on_break'
  openChatCount: number
  avgResponseTimeSeconds: number
  totalChatsToday: number
  avgResolutionTimeSeconds: number
  csatScore: number
}

interface UseAgentsReturn {
  agents: AgentStats[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export const useAgents = (): UseAgentsReturn => {
  const [agents, setAgents] = useState<AgentStats[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiGet<any>('/agents/stats')
      const rawAgents = Array.isArray(response) ? response : (response.agents || [])
      const mapped: AgentStats[] = rawAgents.map((a: any) => ({
        id: a.id,
        name: a.name,
        email: a.email,
        status: a.is_online ? 'online' : 'offline',
        openChatCount: a.open_chats || a.openChatCount || 0,
        avgResponseTimeSeconds: a.avg_response_time_seconds || a.avgResponseTimeSeconds || 0,
        totalChatsToday: a.total_chats_today || a.totalChatsToday || 0,
        avgResolutionTimeSeconds: a.avg_resolution_time_seconds || a.avgResolutionTimeSeconds || 0,
        csatScore: a.csat_score || a.csatScore || 0,
      }))
      setAgents(mapped)
      setError(null)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load agent stats'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    // Refresh every 30 seconds
    const interval = setInterval(refresh, 30000)
    return () => clearInterval(interval)
  }, [refresh])

  return {
    agents,
    loading,
    error,
    refresh
  }
}
